import ExcelJS from "exceljs";

// ── Colors (without leading FF — ExcelJS prepends it) ──────────────────────
const CLR = {
  yellow:  "FFFFF2CC",
  blue:    "FFC9DAF8",
  green:   "FF00FF00",
  white:   "FFFFFFFF",
  holiday: "FFFFFF00",
  title:   "FF2E75B6",
};

const CENTERS = [
  { key: "k",  label: "KIGALI"    },
  { key: "b",  label: "BUTARE"    },
  { key: "ko", label: "KARONGI"   },
  { key: "ru", label: "RUHENGERI" },
  { key: "rw", label: "RWAMAGANA" },
];

const MONTHS_ORDER = [
  "JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE",
  "JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER",
];

const MONTH_SHORT = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

// ── RMTH BB / RNP sheet layout ────────────────────────────────────────────────
// 3 sections: [Jan/Apr/Jul/Oct], [Feb/May/Aug/Nov], [Mar/Jun/Sep/Dec]
// Cols per section: C=M1_sites D=M1_sess E=M1_units  F=M2_sites G=M2_sess …
const RMTH_SECTIONS = [
  [0, 3, 6, 9],
  [1, 4, 7, 10],
  [2, 5, 8, 11],
];
const RMTH_SESS_COLS = [4, 7, 10, 13]; // D G J M (1-based)
const RMTH_UNIT_COLS = [5, 8, 11, 14]; // E H K N

function getRmthMonthInfo(mIdx) {
  for (let sIdx = 0; sIdx < RMTH_SECTIONS.length; sIdx++) {
    const pos = RMTH_SECTIONS[sIdx].indexOf(mIdx);
    if (pos !== -1) return { sIdx, pos };
  }
  return { sIdx: 0, pos: 0 };
}

function ordinal(n) {
  const s = ["th","st","nd","rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fill(argb) {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function boldCenter(cell, bgArgb) {
  cell.font      = { bold: true };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  if (bgArgb) cell.fill = fill(bgArgb);
}

function thinBorder(cell) {
  const side = { style: "thin", color: { argb: "FFD0D0D0" } };
  cell.border = { top: side, bottom: side, left: side, right: side };
}

// ── Column definitions ──────────────────────────────────────────────────────
// A=1 B=2 C=3 D=4 E=5 F=6  G=7  H=8  I=9  J=10 K=11 L=12 M=13 N=14 O=15 P=16 Q=17 R=18 S=19 T=20
const COL_WIDTHS = [3.14, 6.86, 11, 21.57, 5.43, 5.86, 16.57, 5.43, 5.86, 19.14, 5.43, 5.86, 20.86, 5.43, 5.86, 18, 5.43, 5.86, 8.29, 7.57];

// Center column start positions (1-based): D=4, G=7, J=10, M=13, P=16
const CENTER_START = [4, 7, 10, 13, 16]; // col index for Sites col of each center
// Cols: [Sites, Sess, Units] starting at CENTER_START[i]

function colLetter(n) {
  // n is 1-based
  let s = "";
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

// ── Build one month worksheet ───────────────────────────────────────────────
function buildMonthSheet(wb, month, mIdx, { year, viewMode, perfDayMap, centerKeyMap, dailyPlan, monthlyPlanWeeks, activePlanData, DAILY_PLAN_2026, getWeekDaysList, cleanSite }) {
  const ws = wb.addWorksheet(MONTH_SHORT[mIdx]);
  const monthNum = mIdx + 1;

  const showPlanned   = viewMode !== "performed";
  const showPerformed = viewMode !== "planned";

  ws.columns = COL_WIDTHS.map(w => ({ width: w }));

  // ── Row 1: Title ─────────────────────────────────────────────────────────
  ws.addRow(Array(20).fill(""));
  ws.mergeCells("D1:T1");
  const titleCell = ws.getCell("D1");
  titleCell.value = `ABCP ${year} - ${month}`;
  titleCell.font      = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill      = fill(CLR.title);
  ws.getRow(1).height = 22;

  // ── Row 2: Center name headers ───────────────────────────────────────────
  ws.addRow(Array(20).fill(""));
  const r2 = ws.getRow(2);
  ws.mergeCells("B2:C2");
  ws.mergeCells("D2:F2");
  ws.mergeCells("G2:I2");
  ws.mergeCells("J2:L2");
  ws.mergeCells("M2:O2");
  ws.mergeCells("P2:R2");
  ws.mergeCells("S2:T2");

  const r2Labels = [[2, new Date(year, mIdx, 1)], [4,"KIGALI"], [7,"BUTARE"], [10,"KARONGI"], [13,"RUHENGERI"], [16,"RWAMAGANA"], [19,"TOTAL"]];
  r2Labels.forEach(([col, val]) => {
    const cell = r2.getCell(col);
    cell.value = val;
    cell.fill  = fill(col >= 19 ? CLR.green : CLR.yellow);
    boldCenter(cell);
    if (val instanceof Date) cell.numFmt = "DD-MMM-YYYY";
  });
  r2.height = 18;

  // ── Row 3: Column sub-headers ────────────────────────────────────────────
  ws.addRow(Array(20).fill(""));
  const r3 = ws.getRow(3);
  const r3Vals = ["", "DATE","DAYS","Sites","Sess.","Units","Sites","Sess.","Units","Sites","Sess.","Units","Sites","Sess.","Units","Sites","Sess.","Units","SESS.","UNITS"];
  r3Vals.forEach((v, i) => {
    if (!v) return;
    const cell = r3.getCell(i + 1);
    cell.value = v;
    cell.fill  = fill(i >= 18 ? CLR.green : CLR.yellow);
    boldCenter(cell);
    thinBorder(cell);
  });
  r3.height = 16;

  // ── Weeks ────────────────────────────────────────────────────────────────
  const weeks = (monthlyPlanWeeks || []).filter(w => !w.label.startsWith("FIXED"));

  // Track week row ranges for TOTAL MONTH formula
  const weekTotalRows = [];

  weeks.forEach(w => {
    const match   = w.label.match(/WEEK (\d+)/);
    const weekNum = match ? Number(match[1]) : null;
    if (!weekNum) return;

    const daysList = getWeekDaysList(month, weekNum, year);

    // ── Week header ─────────────────────────────────────────────────────
    ws.addRow(Array(20).fill(""));
    const whRow = ws.lastRow;
    ws.mergeCells(`B${whRow.number}:C${whRow.number}`);
    ws.mergeCells(`D${whRow.number}:T${whRow.number}`);
    whRow.getCell(2).value = month;
    whRow.getCell(4).value = `WEEK ${weekNum}`;
    [2, 4].forEach(c => {
      const cell = whRow.getCell(c);
      cell.fill = fill(CLR.blue);
      boldCenter(cell);
    });
    whRow.height = 14;

    const weekDataStart = ws.rowCount + 1;

    // ── Day rows ─────────────────────────────────────────────────────────
    daysList.forEach(({ date, dayName }) => {
      const dp       = dailyPlan[date] || (!activePlanData ? DAILY_PLAN_2026?.[month]?.[date] : undefined);
      const holiday  = dp?.holiday;
      const noColSet = new Set(dp?.noCol || []);

      if (holiday) {
        ws.addRow(Array(20).fill(""));
        const hr = ws.lastRow;
        ws.mergeCells(`B${hr.number}:T${hr.number}`);
        const hc = hr.getCell(2);
        hc.value = holiday;
        hc.fill  = fill(CLR.holiday);
        boldCenter(hc);
        hr.height = 13;
        return;
      }

      // Collect sites per center
      const cSites = CENTERS.map(c => {
        const sites = (dp?.[c.key] || []).map(s => cleanSite(s));
        const noCol = noColSet.has(c.key) && sites.length === 0;
        const planSess  = noCol ? 0 : (dp?.sess?.[c.key]  || 0);
        const planUnits = noCol ? 0 : (dp?.units?.[c.key] || 0);
        const centerId  = centerKeyMap[c.key];
        const dayPerf   = centerId ? perfDayMap?.[centerId]?.[monthNum]?.[date] : null;
        const perfSites = (dayPerf?.siteNames || []).map(s => cleanSite(s));
        const perfSess  = dayPerf?.sessions || 0;
        const perfUnits = dayPerf?.units    || 0;
        return { ...c, sites, noCol, planSess, planUnits, perfSites, perfSess, perfUnits };
      });

      // Displayed sites: prefer performed when mode=performed, else planned
      const dispSites = cSites.map(cs => showPerformed && !showPlanned ? cs.perfSites : cs.sites);
      const maxSites  = Math.max(1, ...dispSites.map(s => s.length));

      // ── Row A: date + day + first site names ──────────────────────────
      ws.addRow(Array(20).fill(""));
      const rowA = ws.lastRow;
      rowA.getCell(2).value = ordinal(date);
      rowA.getCell(3).value = dayName;
      boldCenter(rowA.getCell(2));
      boldCenter(rowA.getCell(3));

      CENTERS.forEach((c, ci) => {
        const cs       = cSites[ci];
        const sitesArr = dispSites[ci];
        const siteCol  = CENTER_START[ci];
        const cell     = rowA.getCell(siteCol);
        cell.value     = cs.noCol ? "No collection" : (sitesArr[0] || "");
        cell.font      = cs.noCol ? { italic: true, color: { argb: "FF888888" } } : {};
        cell.alignment = { wrapText: true, vertical: "middle" };
      });

      // total cols blank in row A
      [19, 20].forEach(c => { rowA.getCell(c).fill = fill(CLR.green); });
      rowA.height = 13;

      // ── Row B: numbers ────────────────────────────────────────────────
      ws.addRow(Array(20).fill(""));
      const rowB = ws.lastRow;

      let totSess = 0, totUnits = 0;
      CENTERS.forEach((c, ci) => {
        const cs      = cSites[ci];
        const siteCol = CENTER_START[ci];
        const sessCol = siteCol + 1;
        const unitCol = siteCol + 2;
        const dispSitesArr = dispSites[ci];

        rowB.getCell(siteCol).value = cs.noCol ? "No collection" : (dispSitesArr[0] || "");
        if (cs.noCol) rowB.getCell(siteCol).font = { italic: true, color: { argb: "FF888888" } };

        const sess  = showPlanned && !showPerformed ? cs.planSess  : showPerformed && !showPlanned ? cs.perfSess  : cs.planSess;
        const units = showPlanned && !showPerformed ? cs.planUnits : showPerformed && !showPlanned ? cs.perfUnits : cs.planUnits;

        if (!cs.noCol) {
          rowB.getCell(sessCol).value  = sess  || 0;
          rowB.getCell(unitCol).value  = units || 0;
          totSess  += sess  || 0;
          totUnits += units || 0;
        }
        [sessCol, unitCol].forEach(col => {
          rowB.getCell(col).alignment = { horizontal: "center", vertical: "middle" };
        });
      });

      rowB.getCell(19).value = totSess;
      rowB.getCell(20).value = totUnits;
      [19, 20].forEach(c => {
        const cell = rowB.getCell(c);
        cell.fill      = fill(CLR.green);
        cell.font      = { bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      rowB.height = 13;

      // Merge date + day across rowA & rowB
      const rA = rowA.number, rB = rowB.number;
      try { ws.mergeCells(`B${rA}:B${rB}`); } catch(_) {}
      try { ws.mergeCells(`C${rA}:C${rB}`); } catch(_) {}

      // Apply borders + fill to data rows
      [rowA, rowB].forEach(row => {
        for (let col = 2; col <= 20; col++) {
          const cell = row.getCell(col);
          if (!cell.fill || cell.fill.pattern === "none") cell.fill = fill(CLR.white);
          thinBorder(cell);
        }
      });

      // ── Extra site rows (when center has > 1 site) ────────────────────
      for (let si = 1; si < maxSites; si++) {
        ws.addRow(Array(20).fill(""));
        const exRow = ws.lastRow;
        exRow.height = 13;
        CENTERS.forEach((c, ci) => {
          const sitesArr = dispSites[ci];
          if (sitesArr[si]) {
            const cell = exRow.getCell(CENTER_START[ci]);
            cell.value = sitesArr[si];
            cell.alignment = { wrapText: true, vertical: "middle" };
          }
        });
        [19, 20].forEach(c => { exRow.getCell(c).fill = fill(CLR.green); });
        for (let col = 2; col <= 20; col++) {
          const cell = exRow.getCell(col);
          if (!cell.fill || cell.fill.pattern === "none") cell.fill = fill(CLR.white);
          thinBorder(cell);
        }
      }
    });

    const weekDataEnd = ws.rowCount;

    // ── TOTAL WEEK row ───────────────────────────────────────────────────
    ws.addRow(Array(20).fill(""));
    const twRow = ws.lastRow;
    ws.mergeCells(`B${twRow.number}:C${twRow.number}`);
    twRow.getCell(2).value = `TOTAL WEEK ${weekNum}`;

    CENTERS.forEach((c, ci) => {
      const siteCol = CENTER_START[ci];
      const sessCol = siteCol + 1;
      const unitCol = siteCol + 2;
      const sessLtr = colLetter(sessCol);
      const unitLtr = colLetter(unitCol);
      twRow.getCell(siteCol).value = "Sites";
      twRow.getCell(sessCol).value = { formula: `SUM(${sessLtr}${weekDataStart}:${sessLtr}${weekDataEnd})` };
      twRow.getCell(unitCol).value = { formula: `SUM(${unitLtr}${weekDataStart}:${unitLtr}${weekDataEnd})` };
    });
    twRow.getCell(19).value = { formula: `SUM(S${weekDataStart}:S${weekDataEnd})` };
    twRow.getCell(20).value = { formula: `SUM(T${weekDataStart}:T${weekDataEnd})` };

    twRow.eachCell((cell, col) => {
      if (col < 2) return;
      cell.fill = fill(col >= 19 ? CLR.green : CLR.yellow);
      boldCenter(cell);
      thinBorder(cell);
    });
    twRow.height = 14;
    weekTotalRows.push(twRow.number);
  });

  // ── TOTAL MONTH row ──────────────────────────────────────────────────────
  ws.addRow(Array(20).fill(""));
  const tmRow = ws.lastRow;
  ws.mergeCells(`B${tmRow.number}:C${tmRow.number}`);
  tmRow.getCell(2).value = `TOTAL ${month}`;

  if (weekTotalRows.length) {
    CENTERS.forEach((c, ci) => {
      const siteCol = CENTER_START[ci];
      const sessCol = siteCol + 1;
      const unitCol = siteCol + 2;
      const sessLtr = colLetter(sessCol);
      const unitLtr = colLetter(unitCol);
      tmRow.getCell(siteCol).value = "SITES";
      const sessRefs = weekTotalRows.map(r => `${sessLtr}${r}`).join("+");
      const unitRefs = weekTotalRows.map(r => `${unitLtr}${r}`).join("+");
      tmRow.getCell(sessCol).value = { formula: sessRefs };
      tmRow.getCell(unitCol).value = { formula: unitRefs };
    });
    const sessRefs = weekTotalRows.map(r => `S${r}`).join("+");
    const unitRefs = weekTotalRows.map(r => `T${r}`).join("+");
    tmRow.getCell(19).value = { formula: sessRefs };
    tmRow.getCell(20).value = { formula: unitRefs };
  }

  tmRow.eachCell((cell, col) => {
    if (col < 2) return;
    cell.fill = fill(col >= 19 ? CLR.green : CLR.yellow);
    boldCenter(cell);
    thinBorder(cell);
  });
  tmRow.height = 16;

  // Freeze header rows
  ws.views = [{ state: "frozen", xSplit: 3, ySplit: 3 }];
  return tmRow.number;
}

// ── RMTH BB / RNP sheet builder ──────────────────────────────────────────────
function buildRmthLikeSheet(wb, sheetName, siteRows) {
  const ws = wb.addWorksheet(sheetName);
  ws.columns = [
    { width: 3 },  { width: 22 }, { width: 16 }, { width: 8 }, { width: 8 },
    { width: 16 }, { width: 8 },  { width: 8 },  { width: 16 },{ width: 8 },
    { width: 8 },  { width: 16 }, { width: 8 },  { width: 8 }, { width: 9 }, { width: 9 },
  ];

  ws.addRow(Array(16).fill(""));
  ws.mergeCells("B1:P1");
  const t1 = ws.getCell("B1");
  t1.value = sheetName;
  t1.font  = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  t1.alignment = { horizontal: "center", vertical: "middle" };
  t1.fill  = fill(CLR.title);
  ws.getRow(1).height = 22;

  const sectionLabels = [
    ["JANUARY","APRIL","JULY","OCTOBER"],
    ["FEBRUARY","MAY","AUGUST","NOVEMBER"],
    ["MARCH","JUNE","SEPTEMBER","DECEMBER"],
  ];
  const monthCols = [3, 6, 9, 12]; // C F I L — start col for each month group
  const sectionEstRows = [];

  sectionLabels.forEach((labels, sIdx) => {
    ws.addRow(Array(16).fill(""));
    const hdr = ws.lastRow;
    hdr.getCell(2).value = "SITES / MONTHS";
    boldCenter(hdr.getCell(2)); hdr.getCell(2).fill = fill(CLR.yellow); thinBorder(hdr.getCell(2));
    labels.forEach((label, i) => {
      const c = monthCols[i];
      ws.mergeCells(`${colLetter(c)}${hdr.number}:${colLetter(c+2)}${hdr.number}`);
      const cell = hdr.getCell(c);
      cell.value = label; boldCenter(cell); cell.fill = fill(CLR.yellow); thinBorder(cell);
    });
    ws.mergeCells(`O${hdr.number}:P${hdr.number}`);
    const tHdr = hdr.getCell(15);
    tHdr.value = "TOTAL"; boldCenter(tHdr); tHdr.fill = fill(CLR.green); thinBorder(tHdr);
    hdr.height = 16;

    ws.addRow(Array(16).fill(""));
    const sub = ws.lastRow;
    sub.getCell(2).fill = fill(CLR.yellow); thinBorder(sub.getCell(2));
    monthCols.forEach(c => {
      sub.getCell(c).value = "Sites"; sub.getCell(c+1).value = "Sess."; sub.getCell(c+2).value = "Units";
      [c, c+1, c+2].forEach(col => { boldCenter(sub.getCell(col)); sub.getCell(col).fill = fill(CLR.yellow); thinBorder(sub.getCell(col)); });
    });
    sub.getCell(15).value = "Sess."; sub.getCell(16).value = "Units";
    [15,16].forEach(col => { boldCenter(sub.getCell(col)); sub.getCell(col).fill = fill(CLR.green); thinBorder(sub.getCell(col)); });
    sub.height = 14;

    const dataStart = ws.rowCount + 1;
    for (let si = 0; si < siteRows; si++) {
      ws.addRow(Array(16).fill(""));
      const row = ws.lastRow;
      for (let col = 2; col <= 16; col++) { row.getCell(col).fill = fill(CLR.white); thinBorder(row.getCell(col)); }
      row.height = 14;
    }
    const dataEnd = ws.rowCount;

    ws.addRow(Array(16).fill(""));
    const est = ws.lastRow;
    est.getCell(2).value = "ESTIMATED SESS."; boldCenter(est.getCell(2)); est.getCell(2).fill = fill(CLR.yellow); thinBorder(est.getCell(2));
    monthCols.forEach(c => {
      const sC = c+1, uC = c+2;
      est.getCell(c).fill = fill(CLR.yellow); thinBorder(est.getCell(c));
      est.getCell(sC).value = { formula: `SUM(${colLetter(sC)}${dataStart}:${colLetter(sC)}${dataEnd})` };
      est.getCell(uC).value = { formula: `SUM(${colLetter(uC)}${dataStart}:${colLetter(uC)}${dataEnd})` };
      [sC, uC].forEach(col => { boldCenter(est.getCell(col)); est.getCell(col).fill = fill(CLR.yellow); thinBorder(est.getCell(col)); });
    });
    est.getCell(15).value = { formula: monthCols.map(c => `${colLetter(c+1)}${est.number}`).join("+") };
    est.getCell(16).value = { formula: monthCols.map(c => `${colLetter(c+2)}${est.number}`).join("+") };
    [15,16].forEach(col => { boldCenter(est.getCell(col)); est.getCell(col).fill = fill(CLR.green); thinBorder(est.getCell(col)); });
    est.height = 14;
    sectionEstRows.push(est.number);
  });

  ws.views = [{ state: "frozen", xSplit: 2, ySplit: 1 }];
  return sectionEstRows; // [s0Row, s1Row, s2Row]
}

// ── Annual Summary sheet builder ─────────────────────────────────────────────
function buildAnnualSummarySheet(wb, year, monthTotalRows, rmthEstRows, rnpEstRows) {
  const ws  = wb.addWorksheet("ANN.SUM");
  const N   = 19; // total columns A-S
  ws.columns = [{ width: 15 }, ...Array.from({ length: 18 }, () => ({ width: 8 }))];

  ws.addRow(Array(N).fill(""));
  ws.mergeCells(`A1:${colLetter(N)}1`);
  const t1 = ws.getCell("A1");
  t1.value = `ANNUAL BLOOD COLLECTION PLAN - ${year} - ALL RCBTs, RMTH BB & RNP`;
  t1.font  = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  t1.alignment = { horizontal: "center", vertical: "middle" };
  t1.fill  = fill(CLR.title);
  ws.getRow(1).height = 24;

  ws.addRow(Array(N).fill(""));
  const r2 = ws.getRow(2);
  r2.getCell(1).value = "MONTH"; boldCenter(r2.getCell(1)); r2.getCell(1).fill = fill(CLR.green); thinBorder(r2.getCell(1));
  ["KIGALI","BUTARE","KARONGI","RUHENGERI","RWAMAGANA","TOTAL RCBTs","RMTH BB","RNP","GRAND TOTAL"].forEach((label, i) => {
    const c1 = 2 + i * 2, c2 = c1 + 1;
    ws.mergeCells(`${colLetter(c1)}2:${colLetter(c2)}2`);
    const cell = r2.getCell(c1);
    cell.value = label; boldCenter(cell); cell.fill = fill(CLR.green); thinBorder(cell);
  });
  r2.height = 18;

  ws.addRow(Array(N).fill(""));
  const r3 = ws.getRow(3);
  r3.getCell(1).fill = fill(CLR.yellow); thinBorder(r3.getCell(1));
  for (let col = 2; col <= N; col++) {
    const cell = r3.getCell(col);
    cell.value = (col % 2 === 0) ? "Sessions" : "Units";
    boldCenter(cell); cell.fill = fill(CLR.yellow); thinBorder(cell);
  }
  r3.height = 14;

  // Source cols in monthly sheets: [sess, units] per center
  const mSess = [5, 8, 11, 14, 17, 19]; // E H K N Q S
  const mUnit = [6, 9, 12, 15, 18, 20]; // F I L O R T
  const rowColors = ["FFFCE4D6", "FFFFF2CC"];

  MONTHS_ORDER.forEach((month, mIdx) => {
    const shRef = MONTH_SHORT[mIdx];
    const tmRow = monthTotalRows[mIdx];
    ws.addRow(Array(N).fill(""));
    const row = ws.lastRow;
    const rn  = row.number;
    const bg  = rowColors[mIdx % 2];

    row.getCell(1).value = month; row.getCell(1).font = { bold: true };
    row.getCell(1).fill  = fill(bg); thinBorder(row.getCell(1));

    mSess.forEach((sc, ci) => {
      const uc = mUnit[ci], destS = 2 + ci * 2, destU = destS + 1;
      row.getCell(destS).value = tmRow ? { formula: `${shRef}!${colLetter(sc)}${tmRow}` } : 0;
      row.getCell(destU).value = tmRow ? { formula: `${shRef}!${colLetter(uc)}${tmRow}` } : 0;
      [destS, destU].forEach(col => { row.getCell(col).fill = fill(bg); row.getCell(col).alignment = { horizontal: "center" }; thinBorder(row.getCell(col)); });
    });

    const { sIdx, pos } = getRmthMonthInfo(mIdx);
    const rmthRow = rmthEstRows?.[sIdx], rnpRow = rnpEstRows?.[sIdx];
    const sCol = colLetter(RMTH_SESS_COLS[pos]), uCol = colLetter(RMTH_UNIT_COLS[pos]);
    row.getCell(14).value = rmthRow ? { formula: `'RMTH BB'!${sCol}${rmthRow}` } : 0;
    row.getCell(15).value = rmthRow ? { formula: `'RMTH BB'!${uCol}${rmthRow}` } : 0;
    row.getCell(16).value = rnpRow  ? { formula: `RNP!${sCol}${rnpRow}` }        : 0;
    row.getCell(17).value = rnpRow  ? { formula: `RNP!${uCol}${rnpRow}` }        : 0;
    [14,15,16,17].forEach(col => { row.getCell(col).fill = fill(bg); row.getCell(col).alignment = { horizontal: "center" }; thinBorder(row.getCell(col)); });

    row.getCell(18).value = { formula: `L${rn}+N${rn}+P${rn}` };
    row.getCell(19).value = { formula: `M${rn}+O${rn}+Q${rn}` };
    [18,19].forEach(col => { row.getCell(col).fill = fill(CLR.green); row.getCell(col).font = { bold: true }; row.getCell(col).alignment = { horizontal: "center" }; thinBorder(row.getCell(col)); });
    row.height = 16;
  });

  ws.addRow(Array(N).fill(""));
  const tr = ws.lastRow;
  tr.getCell(1).value = `TOTAL ${year}`; tr.getCell(1).font = { bold: true }; tr.getCell(1).fill = fill(CLR.green); thinBorder(tr.getCell(1));
  for (let col = 2; col <= N; col++) {
    const ltr = colLetter(col);
    tr.getCell(col).value = { formula: `SUM(${ltr}4:${ltr}15)` };
    tr.getCell(col).fill  = fill(CLR.green); tr.getCell(col).font = { bold: true };
    tr.getCell(col).alignment = { horizontal: "center" }; thinBorder(tr.getCell(col));
  }
  tr.height = 18;
  ws.views = [{ state: "frozen", xSplit: 1, ySplit: 3 }];
}

// ── Public API ───────────────────────────────────────────────────────────────
export async function generateABCPExcel({
  year, viewMode,
  perfDayMap, centerKeyMap,
  abcpConfig, activePlanData,
  MONTHS_WEEKS, DAILY_PLAN_2026,
  getWeekDaysList, cleanSite,
}) {
  const wb          = new ExcelJS.Workbook();
  wb.creator        = "BBMS";
  wb.created        = new Date();
  const monthlyPlan = activePlanData ?? abcpConfig?.monthlyPlan ?? MONTHS_WEEKS;

  const monthTotalRows = [];
  MONTHS_ORDER.forEach((month, mIdx) => {
    const dailyPlan = abcpConfig?.dailyPlan?.[month] || {};
    const tmRowNum = buildMonthSheet(wb, month, mIdx, {
      year, viewMode,
      perfDayMap, centerKeyMap,
      dailyPlan,
      monthlyPlanWeeks: monthlyPlan[month] || [],
      activePlanData,
      DAILY_PLAN_2026,
      getWeekDaysList,
      cleanSite,
    });
    monthTotalRows.push(tmRowNum);
  });

  const rmthEstRows = buildRmthLikeSheet(wb, "RMTH BB", 5);
  const rnpEstRows  = buildRmthLikeSheet(wb, "RNP",     4);
  buildAnnualSummarySheet(wb, year, monthTotalRows, rmthEstRows, rnpEstRows);

  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement("a");
  a.href       = url;
  a.download   = `ABCP-${viewMode}-${year}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
